<template>
  <div class="hubble-law-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$V_rec = H₀ · d$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="220" viewBox="0 0 400 220">
          <!-- Background Grid lines linking to observer -->
          <g stroke="rgba(255, 255, 255, 0.05)" stroke-width="1" stroke-dasharray="2,2">
             <line v-for="g in galaxies" :key="'l'+g.id" :x1="observerX" :y1="observerY" :x2="getGalaxyCoord(g).x" :y2="getGalaxyCoord(g).y" />
          </g>

          <!-- Observer Star/Galaxy -->
          <circle :cx="observerX" :cy="observerY" r="6" fill="#fcd34d" stroke="#fff" stroke-width="1.5">
             <animate attributeName="r" values="6;8;6" dur="2s" repeatCount="indefinite" />
          </circle>
          <text :x="observerX - 30" :y="observerY - 12" fill="#fcd34d" font-size="10" font-weight="bold">中心观察者</text>

          <!-- Galaxies -->
          <g v-for="g in galaxies" :key="g.id">
             <g v-if="g.id !== centerGalaxyId" :transform="`translate(${getGalaxyCoord(g).x}, ${getGalaxyCoord(g).y})`">
                
                <!-- Galaxy body (clickable to change observer) -->
                <g style="cursor: pointer;" @click="setObserver(g)">
                  <ellipse rx="7" ry="3" :fill="getGalaxyColor(g)" :transform="`rotate(${g.angle})`" />
                  <circle r="2" fill="#fff" opacity="0.9" />
                </g>
                
                <!-- Velocity Vector (Relative to observer) -->
                <line x1="0" y1="0" :x2="getVelocityVector(g).vx" :y2="getVelocityVector(g).vy" 
                      stroke="#ef4444" stroke-width="1.5" marker-end="url(#arrow-v)" opacity="0.8" />
                      
                <text x="10" y="5" :fill="getGalaxyColor(g)" font-size="8">v = {{ getRelativeSpeed(g).toFixed(0) }}</text>
             </g>
          </g>

          <defs>
             <marker id="arrow-v" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#ef4444" />
             </marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>哈勃常数 H₀</label>
              <span class="val">{{ expansionRate }} <small>km/s/Mpc</small></span>
           </div>
           <div class="hud-item orange">
              <label>宇宙暴胀特性</label>
              <span class="val" style="font-size:12px;">各向同性 (无中心)</span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-vertical-group">
              <div class="param-row flex flex-row items-center gap-4">
                 <div class="flex-1">
                    <label style="font-size:11px;color:#64748b;font-weight:700;">调节宇宙常数 H₀ (剧烈膨胀或收缩)</label>
                    <el-slider v-model="expansionRate" :min="10" :max="120" size="small"></el-slider>
                 </div>
                 <div class="flex-1">
                    <p style="font-size:11px;color:#64748b;margin:0;">* 直接在上方<strong>点击其他星系</strong>即可切换观察者视角。您会发现无论站在哪里，所有星系都同样在散开！</p>
                 </div>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>真实物理计算：</strong>哈勃定律是宇宙学的基石，说明宇宙正在<strong>均匀且各向同性</strong>地膨胀。退行速度 <code>v</code> 严格正比于到观察者的距离 <code>d</code>。因为正比关系，不仅我们在银河系观测到所有星系在加速远离，<strong>你切换到任何一个星系作为"中心"观测</strong>，都会同样发现其他所有星系都在离它远去！距离远则速度快，多普勒红移效应更强（红色标注）。这说明宇宙爆炸不存在单一原点，而是空间本身的度规在全面拉紧扩张。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HubbleLawViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      expansionRate: 70,
      galaxies: [],
      centerGalaxyId: -1, // -1 means default center of screen (Earth pseudo-position)
      defaultCenter: { x: 200, y: 110 }
    }
  },
  computed: {
    observerX() {
       if (this.centerGalaxyId === -1) return this.defaultCenter.x;
       let g = this.galaxies.find(gx => gx.id === this.centerGalaxyId);
       return g ? g.baseX : this.defaultCenter.x;
    },
    observerY() {
       if (this.centerGalaxyId === -1) return this.defaultCenter.y;
       let g = this.galaxies.find(gx => gx.id === this.centerGalaxyId);
       return g ? g.baseY : this.defaultCenter.y;
    }
  },
  methods: {
    init() {
       // Generate base galaxy locations on a static grid/mesh
       for (let i = 0; i < 20; i++) {
          let r = 20 + Math.random() * 150;
          let th = Math.random() * Math.PI * 2;
          this.galaxies.push({
             id: i,
             baseX: 200 + Math.cos(th)*r,
             baseY: 110 + Math.sin(th)*r,
             angle: Math.random() * 360
          });
       }
    },
    setObserver(g) {
       this.centerGalaxyId = g.id;
    },
    getGalaxyCoord(g) {
       // Isotropic expansion: all space geometry is scaled
       // Expansion formula is applied relative to the observer
       // Actually, the simplest way is to scale the distance from the observer by expansionRate/70
       let dx = g.baseX - this.observerX;
       let dy = g.baseY - this.observerY;
       
       let scale = this.expansionRate / 50;
       
       return {
          // the position of galaxy G relative to new observer, subject to scale
          x: this.observerX + dx * scale,
          y: this.observerY + dy * scale
       };
    },
    getVelocityVector(g) {
       let coord = this.getGalaxyCoord(g);
       // The velocity vector points directly away from observer with magnitude proportional to distance
       let dx = coord.x - this.observerX;
       let dy = coord.y - this.observerY;
       
       // the larger the expansionRate, the larger the speed
       // vector length proportional to expansionRate and distance
       let vFactor = this.expansionRate / 200; 
       return {
          vx: dx * vFactor,
          vy: dy * vFactor
       };
    },
    getRelativeSpeed(g) {
       let vec = this.getVelocityVector(g);
       return Math.sqrt(vec.vx*vec.vx + vec.vy*vec.vy) * 10;
    },
    getGalaxyColor(g) {
       let speed = this.getRelativeSpeed(g);
       // Map speed to redshift. Low speed -> white/blueish (no shift), High speed -> extremely red
       // Original resting color is roughly hsl(200, 70%, 60%) (blueish). 
       // Shift goes from 200 down to 0 (red)
       let shift = Math.min(200, Math.max(0, 200 - speed * 1.5));
       return `hsl(${shift}, 80%, 65%)`;
    }
  },
  mounted() { this.init(); }
}
</script>

<style scoped lang="scss">
.hubble-law-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f9ff; color: #0891b2; border: 1px solid #cffafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 220px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #94a3b8; font-weight: normal; } } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-row { flex-direction: row; } .items-center { align-items: center; } .gap-4 { gap: 1rem; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #2563eb; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
